import { create } from 'zustand';

/**
 * Global single-account scope, matching the old Blazor app's `IAppSession.Filter.Account`.
 *
 *   - empty string = "no account selected yet" (the AccountInitializer will fill it in once the
 *     accounts list resolves; nothing renders account-bound data while it's empty in practice
 *     because every consumer hook skips its `account` param when undefined).
 *   - non-empty string = the selected account name. Every page that filters by account reads
 *     from `useAccount()` so flipping the value here invalidates every account-keyed query
 *     in TanStack Query automatically.
 *
 * Persisted to localStorage so the selection survives a hard refresh. We don't use Zustand's
 * `persist` middleware because we want hydration to happen synchronously at module-load (so
 * the first render of any page already has the right account in hand — no flash of empty data).
 */
const STORAGE_KEY = 'journal:account';

function readInitial(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

interface AccountState {
  account: string;
  setAccount: (next: string) => void;
}

export const useAccountStore = create<AccountState>(set => ({
  account: readInitial(),
  setAccount: (next: string) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* quota / private mode */ }
    set({ account: next });
  },
}));

/** Hook returning the currently-selected account name (empty string if none yet). */
export const useAccount    = () => useAccountStore(s => s.account);
export const useSetAccount = () => useAccountStore(s => s.setAccount);
